/** Buffers that are not sound effects: the reverb's impulse response and the noise the wind is made of. Pure. */

import { mulberry32 } from './rng';

/** Backed by a real `ArrayBuffer` (never a shared one), which is what `AudioBuffer.copyToChannel` takes. */
export type Samples = Float32Array<ArrayBuffer>;

/**
 * A synthetic outdoor impulse response: a few early slaps, then noise dying away and losing its
 * top as it goes. Two decorrelated channels, so the tail is wide even on a mono source.
 */
export function renderImpulse(sampleRate: number, seconds: number, seed: number): [Samples, Samples] {
  const n = Math.floor(sampleRate * seconds);
  const render = (channelSeed: number): Samples => {
    const rand = mulberry32(channelSeed);
    const out = new Float32Array(n);
    let lp = 0;
    for (let i = 0; i < n; i++) {
      const t = i / n;
      const decay = Math.pow(1 - t, 3.2);
      // The low-pass closes as the tail decays: far echoes are dull echoes.
      const a = 0.65 - 0.55 * t;
      lp += a * (rand() * 2 - 1 - lp);
      out[i] = lp * decay;
    }
    for (let k = 0; k < 5; k++) {
      const at = Math.floor((0.011 + rand() * 0.06) * sampleRate);
      if (at < n) out[at] = (out[at] ?? 0) + (rand() > 0.5 ? 0.7 : -0.7) * (1 - k * 0.15);
    }
    return out;
  };
  return [render(seed), render(seed ^ 0x5bd1e995)];
}

/** Seconds of plain noise to loop. White loops seamlessly by nature: any seam is itself just noise. */
export function renderNoise(sampleRate: number, seconds: number, seed: number): Samples {
  const rand = mulberry32(seed);
  const out = new Float32Array(Math.floor(sampleRate * seconds));
  for (let i = 0; i < out.length; i++) out[i] = rand() * 2 - 1;
  return out;
}
